/**
 * How deep the chart says the water is at a point.
 *
 * One rule, in one place, because getting it wrong is quiet and expensive in
 * both directions: **the largest-scale band with coverage is the answer.**
 *
 * Depth areas tile the chart, so a query at any point returns one polygon per
 * usage band, and the bands disagree by design. A general chart generalises: in
 * Halifax approaches, band 2 cell CA23MQ8B carries a single 0–18.3 m polygon
 * over water that band 5 surveys as 20–30 m. That is not an error in the data —
 * it is what a small-scale chart is for.
 *
 * Taking the shallowest across bands therefore reads "zero metres" over most of
 * a harbour approach. The forward scan did exactly that and marked shallow water
 * in sixty feet, next to soundings saying so, while the chart underneath was
 * shaded deep. A warning that contradicts the display in front of the skipper is
 * worse than no warning: it is the one that teaches them to ignore the next one.
 *
 * Within a single band, shallowest still wins — two areas can meet at the
 * queried pixel, and of two possible answers the shallow one cannot mislead.
 *
 * This is the same rule the renderer follows (bands drawn ascending, largest
 * scale on top) and the same one the feature panel follows when a tap returns
 * one depth per band. All three must agree or the app contradicts itself.
 */

import type { MapGeoJSONFeature } from 'maplibre-gl';

export interface ChartedDepth {
  /**
   * Shallow limit of the depth area, metres. **Null means the chart does not
   * say**, which `isUnsafeDepth` treats as unsafe — never read it as zero and
   * never read it as deep.
   */
  depthM: number | null;
  /** Usage band the answer came from; larger is a larger-scale survey. */
  band: number;
}

const num = (value: unknown): number | null =>
  (typeof value === 'number' && Number.isFinite(value) ? value : null);

/**
 * Resolve a stack of depth-area features to one depth.
 *
 * Returns null when the stack is empty — no coverage, or more often a tile that
 * has not loaded. That is not "safe" and not "shallow"; it is "no answer", and
 * callers must skip it rather than conclude anything.
 */
export function chartedDepthAt(features: readonly MapGeoJSONFeature[]): ChartedDepth | null {
  if (features.length === 0) return null;

  // A feature with no band at all sorts below every real one rather than above:
  // if some other band is present and labelled, prefer the labelled one.
  const bandOf = (feature: MapGeoJSONFeature): number =>
    num((feature.properties ?? {}).band) ?? -1;

  const best = features.reduce((max, feature) => Math.max(max, bandOf(feature)), -Infinity);

  const depths = features
    .filter((feature) => bandOf(feature) === best)
    .map((feature) => num((feature.properties ?? {}).drval1));

  // An unstated shallow limit inside the winning band wins outright: unknown is
  // unsafe, and letting a numbered sibling override it would be reading the
  // silence as a depth.
  if (depths.some((value) => value === null)) return { depthM: null, band: best };

  return {
    depthM: (depths as number[]).reduce((min, value) => (value < min ? value : min)),
    band: best,
  };
}
